import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { Modal } from '@saucelabs/sl-component-overlay';
import { Button } from '@saucelabs/ds-button';
import { Tooltip } from '@saucelabs/ds-tooltip';
import {
  IconCloseFill,
  IconManualFill,
  IconLockFill,
  IconPinFill,
  IconPrivateDeviceFill,
  IconUnpinFill,
} from '@saucelabs/ds-icons';

import { DeviceDescriptor } from '../records';
import { useStartTestButtonState } from '../hooks/useStartTestButtonState';
import TrialDiagonal from './TrialDiagonal';
import Styles from './DeviceDetailModal.styles';

const context = classNames.bind(Styles);

// Shows the in-depth details of a device a user could test on.
const DeviceDetailModal = ({
  device,
  email,
  hasSuitableAppConfigurationSelected,
  isFreemium,
  isFreeTrial,
  isInUse,
  isLocked,
  isManualMinutesExpired,
  onClose,
  onLaunch,
  pinned,
  show,
  unverified,
  ...other
}) => {
  const deviceSpecs = Object.entries(device.getFullSpecifications());
  const detailsTableRows = deviceSpecs.map(([propertyName, propertyVal]) => (
    <tr key={`device-detail-prop-${propertyName.replace(/\s/g, '')}`}>
      <th className="px-1.5 py-px text-gray-700">
        {propertyName}
      </th>
      <td className="px-1.5 py-px text-gray-700">
        {propertyVal || '-'}
      </td>
    </tr>
  ));

  const deviceName = device.name;
  const { enableAdvise, isButtonDisabled, isDevicePermitted } = useStartTestButtonState({
    email,
    hasSuitableAppConfigurationSelected,
    isInUse,
    isLocked,
    isManualMinutesExpired,
    unverified,
  });

  return (
    <Modal
      centered
      show={show}
      {...other}
    >
      <button
        aria-label="Close"
        className="absolute bg-transparent border-none cursor-pointer fill-current p-4 right-0 top-0 text-gray-600"
        data-qa="device-detail-modal-close-button"
        onClick={onClose}
        type="button"
      >
        <IconCloseFill className="h-1.5 w-1.5" />
      </button>
      <Modal.Header>
        <Modal.Title className="text-high-emphasis">
          {deviceName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={context(Styles['modal-body'], 'overflow-hidden')}>
        <TrialDiagonal
          isFreemium={isFreemium}
          isFreeTrial={isFreeTrial}
        />
        <div className={Styles['image-and-prefs']}>
          <div className={Styles['image-wrapper']}>
            <div className={context({ 'image-wrapper-in-use': isInUse })}>
              <img
                alt={`Front of the ${deviceName}.`}
                className={context('device-image', { 'filter grayscale': isInUse })}
                src={device.getImageUrl()}
              />
              {isInUse && (
                <div
                  className={Styles['in-use']}
                  data-qa="device-in-use"
                >
                  in use
                </div>
              )}
            </div>
          </div>
          {isLocked && (
            <div className="text-center text-medium-emphasis">
              <IconLockFill className="align-middle fill-current" />
              <span className="align-middle"> UPGRADE TO UNLOCK</span>
            </div>
          )}
          <div className="text-center text-medium-emphasis">
            {pinned ?
              <IconUnpinFill className="align-middle fill-current mr-1 text-blue-700" /> :
              <IconPinFill className="align-middle fill-current mr-1 text-gray-400" />}
            <span className="align-middle">{pinned ? 'PINNED' : 'UNPINNED'}</span>
          </div>
          {device.isPrivateDevice() && (
            <div className="text-center text-medium-emphasis">
              <IconPrivateDeviceFill className="align-middle fill-current text-green-600" />
              <span className="align-middle"> PRIVATE</span>
            </div>
          )}
        </div>
        <div className={Styles['details-table-wrapper']}>
          <table className={Styles['details-table']}>
            <tbody>
              {detailsTableRows}
            </tbody>
          </table>
        </div>
      </Modal.Body>

      <Modal.Footer className={Styles.footer}>
        <Tooltip
          delay={300}
          isOpen={!isDevicePermitted ? undefined : false}
          placement="top"
          title={enableAdvise}
        >
          <div>
            <Button
              className="relative"
              data-qa="device-detail-modal-start-test-button"
              disabled={isButtonDisabled}
              onPress={onLaunch}
            >
              <IconManualFill className="fill-current h-2 mr-0.5" />
              <span>Start Test</span>
            </Button>
          </div>
        </Tooltip>
      </Modal.Footer>
    </Modal>
  );
};

DeviceDetailModal.propTypes = {
  device: PropTypes.instanceOf(DeviceDescriptor).isRequired,
  email: PropTypes.string,
  hasSuitableAppConfigurationSelected: PropTypes.bool,
  isFreemium: PropTypes.bool,
  isFreeTrial: PropTypes.bool,
  isInUse: PropTypes.bool,
  isLocked: PropTypes.bool,
  isManualMinutesExpired: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onLaunch: PropTypes.func.isRequired,
  pinned: PropTypes.bool,
  // meaning "show modal"; passed onto <Modal />
  show: PropTypes.bool.isRequired,
  unverified: PropTypes.bool,
};

DeviceDetailModal.defaultProps = {
  email: undefined,
  hasSuitableAppConfigurationSelected: true,
  isFreemium: false,
  isFreeTrial: false,
  isInUse: false,
  isLocked: false,
  isManualMinutesExpired: false,
  pinned: false,
  unverified: false,
};

export default DeviceDetailModal;
